import React, { useRef, useState, useEffect } from 'react';
import styled from 'styled-components';
import { uploadCompressedProfileImage, deleteProfileImage } from '../services/firebase';

const ImageContainer = styled.div`
  position: relative;
  margin-bottom: 24px;
`;

const ProfileImage = styled.div`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background-color: #d1d5db;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  background-image: ${props => props.$imageUrl ? `url(${props.$imageUrl})` : 'none'};
  background-size: cover;
  background-position: center;
`;

const ProfileIcon = styled.div`
  width: 60px;
  height: 60px;
  background-image: url("data:image/svg+xml,%3csvg width='60' height='60' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3e%3cpath d='M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z' fill='%23ffffff'/%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  display: ${props => props.$hasImage ? 'none' : 'block'};
`;

const EditButton = styled.div`
  position: absolute;
  bottom: 0;
  right: 0;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: #00BBD3;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.3s ease;

  &:hover {
    background-color: #009bb3;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background-color: #d1d5db;
  }
`;

const EditIcon = styled.div`
  width: 20px;
  height: 20px;
  background-image: url("data:image/svg+xml,%3csvg width='20' height='20' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3e%3cpath d='M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z' fill='white'/%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
`;

const HiddenInput = styled.input`
  display: none;
`;

const UploadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  border-radius: 50%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 12px;
  font-weight: bold;
  font-family: 'Noto Sans Thai', sans-serif;
`;

const Spinner = styled.div`
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-top: 3px solid white;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  animation: spin 1s linear infinite;
  margin-bottom: 8px;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const RemoveButton = styled.button`
  position: absolute;
  top: -8px;
  right: -8px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background-color: #ff4444;
  color: white;
  border: 2px solid white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 18px;
  font-weight: bold;
  transition: background-color 0.3s ease;
  z-index: 10;

  &:hover {
    background-color: #cc0000;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ProfileImageUpload = ({
  imageUrl,
  onImageChange,
  userId,
  disabled = false
}) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [currentImageUrl, setCurrentImageUrl] = useState(imageUrl);

  // Sync state with prop when imageUrl changes
  useEffect(() => {
    console.log('🖼️ ProfileImageUpload - imageUrl prop changed:', imageUrl);
    setCurrentImageUrl(imageUrl);
  }, [imageUrl]);

  const handleEditClick = () => {
    if (!disabled && !uploading) {
      fileInputRef.current?.click();
    }
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setUploading(true);
      console.log('🖼️ ProfileImageUpload - Starting upload for userId:', userId);

      // Upload to Firebase
      const downloadURL = await uploadCompressedProfileImage(file, userId);
      console.log('✅ ProfileImageUpload - Upload successful! URL:', downloadURL);

      // Update state
      setCurrentImageUrl(downloadURL);
      console.log('🖼️ ProfileImageUpload - Updated currentImageUrl state');

      // Notify parent component
      if (onImageChange) {
        onImageChange(downloadURL);
        console.log('🖼️ ProfileImageUpload - Notified parent component with URL');
      }

      // Clear input
      event.target.value = '';
    } catch (error) {
      console.error('❌ ProfileImageUpload - Upload error:', error);
      alert(error.message || 'เกิดข้อผิดพลาดในการอัปโหลดรูปภาพ');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = async (e) => {
    e.stopPropagation();

    if (!currentImageUrl || disabled || uploading) return;

    const confirmed = window.confirm('ต้องการลบรูปโปรไฟล์หรือไม่?');
    if (!confirmed) return;

    try {
      setUploading(true);

      // Delete from Firebase
      await deleteProfileImage(currentImageUrl);

      // Update state
      setCurrentImageUrl(null);

      // Notify parent component
      if (onImageChange) {
        onImageChange(null);
      }
    } catch (error) {
      console.error('Delete error:', error);
      alert('เกิดข้อผิดพลาดในการลบรูปภาพ');
    } finally {
      setUploading(false);
    }
  };

  return (
    <ImageContainer>
      <ProfileImage $imageUrl={currentImageUrl}>
        <ProfileIcon $hasImage={!!currentImageUrl} />
        {uploading && (
          <UploadingOverlay>
            <Spinner />
            กำลังอัปโหลด...
          </UploadingOverlay>
        )}
      </ProfileImage>

      <EditButton
        onClick={handleEditClick}
        disabled={disabled || uploading}
        as={disabled || uploading ? 'div' : 'div'}
      >
        <EditIcon />
      </EditButton>

      {currentImageUrl && !uploading && (
        <RemoveButton
          onClick={handleRemoveImage}
          disabled={disabled || uploading}
          title="ลบรูปภาพ"
        >
          ×
        </RemoveButton>
      )}

      <HiddenInput
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/webp"
        onChange={handleFileChange}
        disabled={disabled || uploading}
      />
    </ImageContainer>
  );
};

export default ProfileImageUpload;
